import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function OwnerSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selfService, setSelfService] = useState(false);
  const [termsUrl, setTermsUrl] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState('');

  useEffect(() => {
    api.get('/settings').then(r => {
      const s = r.data;
      setSettings(s);
      setSelfService(s.self_service_enabled || false);
      setTermsUrl(s.terms_url || '');
      setPrivacyUrl(s.privacy_url || '');
    }).catch(() => { toast.error('Unable to load settings — owner access required'); })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/settings', {
        self_service_enabled: selfService,
        terms_url: termsUrl,
        privacy_url: privacyUrl,
      });
      setSettings(r.data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="owner-settings-page">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate('/home')} className="text-white p-1"><ArrowLeft className="w-5 h-5" weight="bold" /></button>
        <h1 className="text-lg text-white font-bold">Platform Settings</h1>
      </div>
      <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
        <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-[#1D242B] text-sm">Self-service signup</p>
              <p className="text-xs text-[#455561]">Allow organisers to sign up and create events</p>
            </div>
            <Switch checked={selfService} onCheckedChange={setSelfService} data-testid="toggle-self-service" />
          </div>
        </Card>

        <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-4">
          <h2 className="font-bold text-[#1D242B] text-sm">Legal documents</h2>
          <div>
            <label className="text-xs font-semibold text-[#455561] mb-1 block">Terms of Use and Sale URL</label>
            <Input value={termsUrl} onChange={e => setTermsUrl(e.target.value)} placeholder="https://..." data-testid="terms-url-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#455561] mb-1 block">Privacy Policy URL</label>
            <Input value={privacyUrl} onChange={e => setPrivacyUrl(e.target.value)} placeholder="https://..." data-testid="privacy-url-input" />
          </div>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-[#1B4332] text-white rounded-xl font-semibold" data-testid="save-settings">
          {saving ? 'Saving...' : 'Save settings'}
        </Button>
      </div>
    </div>
  );
}
